import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useReactions } from '@/hooks/useReactions';
import { mineThenPublish } from '@/lib/publishPow';
import { REACTION_POW } from '@/lib/stashstr';
import { cn } from '@/lib/utils';

/** Stasher-style block arrow (▲ / ▼), 26px like the original. */
function VoteArrowIcon({ direction, size = 26 }: { direction: 'up' | 'down'; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d={
          direction === 'up'
            ? 'M12 3 L20 12 H15 V21 H9 V12 H4 Z'
            : 'M12 21 L20 12 H15 V3 H9 V12 H4 Z'
        }
      />
    </svg>
  );
}

interface VoteArrowsProps {
  targetId: string;
  targetKind: '11' | '1111';
  targetPubkey: string;
  compact?: boolean;
  /** Hard-disable both arrows (e.g. unproven posts — votes would vanish). */
  disabled?: boolean;
}

/**
 * Stasher-style vote column: ▲ / net score / ▼.
 * Up = green (#198754), down = red (#dc3545), idle gray #a5a5a5.
 * 10-bit PoW per vote; latest-wins switching (up -> down drops the up).
 */
export function VoteArrows({ targetId, targetKind, targetPubkey, compact, disabled }: VoteArrowsProps) {
  const { user } = useCurrentUser();
  const publish = useNostrPublish();
  const queryClient = useQueryClient();

  const { data } = useReactions(targetId, user?.pubkey, targetKind);

  const [mining, setMining] = useState(false);
  const [tries, setTries] = useState(0);
  const cancelRef = useRef(false);

  const score = data?.score ?? 0;
  const myVote = data?.myVote;

  const vote = async (direction: 'up' | 'down') => {
    if (!user || mining || myVote === direction) return;
    setMining(true);
    cancelRef.current = false;
    setTries(0);
    try {
      await mineThenPublish({
        userPubkey: user.pubkey,
        publish: publish.mutateAsync,
        kind: 7,
        content: direction === 'up' ? '+' : '-',
        // Dual-case reference tags: relays index lowercase only; uppercase
        // kept for NIP-22-style clients that read E/K/P.
        tags: [
          ['E', targetId],
          ['e', targetId],
          ['K', targetKind],
          ['k', targetKind],
          ['P', targetPubkey],
          ['p', targetPubkey],
        ],
        difficulty: REACTION_POW,
        onProgress: setTries,
        shouldCancelRef: cancelRef,
      });
      // Refresh this target's tally + feeds (Top freshness)
      queryClient.invalidateQueries({ queryKey: ['stashstr', 'reactions', targetId] });
      queryClient.invalidateQueries({ queryKey: ['stashstr', 'feed'] });
    } catch {
      // cancelled or failed — bar resets below
    } finally {
      setMining(false);
      setTries(0);
    }
  };

  const arrowSize = compact ? 20 : 26;

  // Mining renders in-column: arrows stay visible (disabled), score dims,
  // tries + cancel render under the score. No layout jump.

  return (
    <div
      className={cn(
        'flex flex-col items-center shrink-0 transition-opacity',
        compact ? 'w-7' : 'w-9',
        mining && 'opacity-60',
      )}
    >
      <button
        type="button"
        aria-label="Upvote"
        aria-pressed={myVote === 'up'}
        disabled={disabled || !user || mining || myVote === 'up'}
        title={
          disabled
            ? 'Unproven post — votes hidden until PoW verifies'
            : mining
              ? 'Mining…'
              : myVote === 'up'
                ? 'You upvoted this'
                : 'Upvote'
        }
        onClick={() => vote('up')}
        className={cn(
          'p-0.5 rounded leading-none',
          myVote === 'up'
            ? 'text-[#198754]'
            : 'text-[#a5a5a5] hover:text-gray-700 dark:hover:text-gray-300',
          !user && 'cursor-not-allowed',
        )}
      >
        <VoteArrowIcon direction="up" size={arrowSize} />
      </button>

      {mining && compact ? (
        <Loader2 className="size-3 my-0.5 animate-spin text-gray-400 dark:text-gray-500" />
      ) : (
        <span
          className={cn(
            'font-bold leading-tight',
            compact ? 'text-[11px]' : 'text-[13px]',
            score > 0
              ? 'text-[#198754]'
              : score < 0
                ? 'text-[#dc3545]'
                : 'text-gray-500 dark:text-gray-400',
          )}
        >
          {score}
        </span>
      )}

      {mining && !compact && (
        <span className="text-[10px] text-gray-400 dark:text-gray-500 leading-none">
          {tries > 0 ? tries : '…'}
        </span>
      )}

      <button
        type="button"
        aria-label="Downvote"
        aria-pressed={myVote === 'down'}
        disabled={disabled || !user || mining || myVote === 'down'}
        title={
          disabled
            ? 'Unproven post — votes hidden until PoW verifies'
            : mining
              ? 'Mining…'
              : myVote === 'down'
                ? 'You downvoted this'
                : 'Downvote'
        }
        onClick={() => vote('down')}
        className={cn(
          'p-0.5 rounded leading-none',
          myVote === 'down'
            ? 'text-[#dc3545]'
            : 'text-[#a5a5a5] hover:text-gray-700 dark:hover:text-gray-300',
          !user && 'cursor-not-allowed',
        )}
      >
        <VoteArrowIcon direction="down" size={arrowSize} />
      </button>

      {mining && !compact && (
        <button
          type="button"
          onClick={() => {
            cancelRef.current = true;
          }}
          title="Cancel mining"
          aria-label="Cancel mining"
          className="text-[10px] text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 leading-none mt-0.5"
        >
          ×
        </button>
      )}
    </div>
  );
}
